import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import styles from './platform-pages.module.css';

/**
 * The frame around the three integration screens.
 *
 * WHY THIS AREA IS SPLIT AT ALL
 * "CampusOne Core connects to EduCore in production — is it healthy?" and "which
 * EduCore school is Kabale Secondary?" are different questions. They have
 * different answers, different owners and — the part that used to be impossible to
 * put on screen — they fail independently. The Digital Switch can be perfectly
 * healthy while one school's tenant mapping has never been filled in, and the
 * previous single page could not express that: the institution selector was the
 * only way into the whole area, so every environment-level fact was reported as
 * though it belonged to whichever school you happened to pick first.
 *
 * So: connections are environment-level and are reached without choosing anybody.
 * Mappings are institution-level and live under the institution. Health is the
 * cross-cutting view of both.
 *
 * WHY LINKS RATHER THAN role="tab"
 * These tabs are routes. Each one is bookmarkable, back and forward move between
 * them, and one of them lives under a different URL branch entirely
 * (/admin/institutions/:id/integrations). The ARIA tab pattern describes panels
 * swapped inside one document and asks for arrow-key roving with the tab stop on
 * the selected tab — applied to navigation it takes working links, removes them
 * from the tab order, and announces them as something they are not. A labelled
 * <nav> of links with aria-current on the active one is keyboard navigable by
 * construction, announces the destination, and survives a page reload.
 */

export type IntegrationsTab = 'connections' | 'mappings' | 'health';

interface IntegrationsAreaProps {
  active: IntegrationsTab;
  /** Set on the mappings tab so its link keeps the institution already chosen. */
  institutionId?: string;
  title: string;
  lead: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function IntegrationsArea({
  active,
  institutionId,
  title,
  lead,
  actions,
  children,
}: IntegrationsAreaProps) {
  const { t } = useTranslation();

  const mappingsTo = institutionId
    ? `/admin/institutions/${encodeURIComponent(institutionId)}/integrations`
    : '/admin/integrations/mappings';

  const tabs: readonly { key: IntegrationsTab; to: string; labelKey: string }[] = [
    { key: 'connections', to: '/admin/integrations', labelKey: 'platform.integrations.tab.connections' },
    { key: 'mappings', to: mappingsTo, labelKey: 'platform.integrations.tab.mappings' },
    { key: 'health', to: '/admin/integrations/health', labelKey: 'platform.integrations.tab.health' },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{title}</h1>
          <p className={styles.pageLead}>{lead}</p>
        </div>
        {actions}
      </header>

      <nav className={styles.tabBar} aria-label={t('platform.integrations.tab.navLabel')}>
        {/*
          Plain links with the current tab named explicitly, rather than NavLink's
          URL matching. The mappings tab is the current tab at
          /admin/institutions/:id/integrations, which is not the URL its own link
          points to when no institution has been chosen — NavLink would leave that
          tab unmarked on the very screen it belongs to.
        */}
        {tabs.map((tab) => (
          <Link
            key={tab.key}
            to={tab.to}
            className={`${styles.tab} ${active === tab.key ? styles.tabActive : ''}`}
            aria-current={active === tab.key ? 'page' : undefined}
          >
            {t(tab.labelKey)}
          </Link>
        ))}
      </nav>

      {/*
        Stated once, above every tab, because the separation only helps if the
        reader knows it exists. An operator who believes a green connection means
        a school is wired up will not go looking for the missing mapping.
      */}
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.integrations.separationNotice')}
      </p>

      {children}
    </div>
  );
}
